import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [result, setResult] = useState(null);
  const [failed, setFailed] = useState(false);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      while (!cancelled && attempts.current < 15) {
        attempts.current += 1;
        try {
          const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
          if (data.payment_status === "paid") {
            if (!cancelled) setResult(data);
            return;
          }
          if (["failed", "expired"].includes(data.payment_status)) {
            if (!cancelled) setFailed(true);
            return;
          }
        } catch {
          if (!cancelled) setFailed(true);
          return;
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setFailed(true);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-lg px-4 pb-24 pt-32 sm:px-6" data-testid="payment-success-page">
      {!result && !failed && (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="payment-polling">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-900" aria-hidden="true" />
          <h1 className="mt-5 font-display text-2xl font-bold tracking-tight text-slate-900">
            Confirming your payment…
          </h1>
          <p className="mt-2 text-sm text-slate-600">This takes a few seconds. Don't close this page.</p>
        </div>
      )}

      {result && (
        <div className="rounded-xl border border-green-200 bg-white p-10 text-center shadow-sm" data-testid="payment-confirmed">
          <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" aria-hidden="true" />
          <h1 className="mt-5 font-display text-2xl font-bold tracking-tight text-slate-900">
            Payment confirmed
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Your booking payment was verified by the server. A receipt has been emailed to you.
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <StatusBadge value={result.booking_payment_status || "paid"} testId="stripe-payment-badge" />
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {result.booking_id && (
              <Link
                to={`/receipt/${result.booking_id}`}
                data-testid="stripe-receipt-link"
                className="rounded-md border-2 border-green-300 px-5 py-2.5 text-sm font-medium text-green-700 hover:border-green-500"
              >
                View receipt
              </Link>
            )}
            <Link
              to="/my-bookings"
              data-testid="stripe-my-bookings-link"
              className="rounded-md bg-blue-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800"
            >
              My Bookings
            </Link>
          </div>
        </div>
      )}

      {failed && (
        <div className="rounded-xl border border-red-200 bg-white p-10 text-center shadow-sm" data-testid="payment-failed">
          <XCircle className="mx-auto h-12 w-12 text-red-500" aria-hidden="true" />
          <h1 className="mt-5 font-display text-2xl font-bold tracking-tight text-slate-900">
            Payment not completed
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            No money was captured. You can retry anytime from My Bookings.
          </p>
          <Link
            to="/my-bookings"
            data-testid="stripe-retry-link"
            className="mt-8 inline-block rounded-md bg-blue-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800"
          >
            Go to My Bookings
          </Link>
        </div>
      )}
    </div>
  );
}
